import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaCalendarAlt,
  FaCheck,
  FaHashtag,
  FaPen,
  FaPlus,
  FaSearch,
  FaTrash,
  FaUniversity
} from 'react-icons/fa';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  PageHeader
} from '../shared/ui';
import Modal from '../components/Modal';
import { PopupContext } from '../App';
import { getRates, addRate, updateRate } from '../services/rateService';
import styles from './RatesPage.module.css';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' }
];

const EMPTY_FORM = {
  id: null,
  name: '',
  date_from: '',
  date_to: '',
  rate_0_17m: '',
  rate_18_29m: '',
  rate_30_59m: '',
  rate_60_156m: '',
  is_active: true
};

function formatUsd(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
}

function sliceDate(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function parseMoneyPayload(form) {
  const n = (v) => {
    const x = parseFloat(String(v).trim());
    return Number.isFinite(x) ? x : 0;
  };
  return {
    name: form.name.trim(),
    date_from: form.date_from || null,
    date_to: form.date_to || null,
    rate_0_17m: n(form.rate_0_17m),
    rate_18_29m: n(form.rate_18_29m),
    rate_30_59m: n(form.rate_30_59m),
    rate_60_156m: n(form.rate_60_156m),
    is_active: !!form.is_active
  };
}

export default function RatesPage() {
  const showPopup = useContext(PopupContext);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmInactive, setConfirmInactive] = useState(null);

  const refresh = () => {
    setLoading(true);
    getRates()
      .then((data) => setRates(Array.isArray(data) ? data : []))
      .catch(() => setRates([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const counts = useMemo(() => {
    const c = { all: rates.length, active: 0, inactive: 0 };
    for (const r of rates) {
      if (r.is_active) c.active += 1;
      else c.inactive += 1;
    }
    return c;
  }, [rates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rates.filter((rate) => {
      if (filter === 'active' && !rate.is_active) return false;
      if (filter === 'inactive' && rate.is_active) return false;
      if (!q) return true;
      const hay = [rate.name, rate.date_from, rate.date_to].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rates, search, filter]);

  const setField = (key) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (rate) => {
    setForm({
      id: rate.id,
      name: rate.name || '',
      date_from: sliceDate(rate.date_from),
      date_to: sliceDate(rate.date_to),
      rate_0_17m: rate.rate_0_17m ?? '',
      rate_18_29m: rate.rate_18_29m ?? '',
      rate_30_59m: rate.rate_30_59m ?? '',
      rate_60_156m: rate.rate_60_156m ?? '',
      is_active: rate.is_active !== false
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      showPopup('Please enter a name for this rate schedule.', 'error');
      return;
    }
    if (!form.date_from || !form.date_to) {
      showPopup('Start and end dates are required.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = parseMoneyPayload(form);
      if (form.id) {
        await updateRate(form.id, payload);
        showPopup('Rate schedule updated', 'success');
      } else {
        await addRate(payload);
        showPopup('Rate schedule added', 'success');
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      refresh();
    } catch (err) {
      showPopup(err?.message || 'Failed to save rate schedule', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmInactive = async () => {
    if (!confirmInactive) return;
    const rate = confirmInactive;
    setSubmitting(true);
    try {
      await updateRate(rate.id, {
        name: rate.name,
        date_from: rate.date_from,
        date_to: rate.date_to,
        rate_0_17m: rate.rate_0_17m,
        rate_18_29m: rate.rate_18_29m,
        rate_30_59m: rate.rate_30_59m,
        rate_60_156m: rate.rate_60_156m,
        is_active: false
      });
      showPopup('Rate schedule set inactive', 'success');
      setConfirmInactive(null);
      refresh();
    } catch (err) {
      showPopup(err?.message || 'Failed to set inactive', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Voucher rates"
        description="Daily reimbursement amounts by child age (in months). Active schedules cannot overlap dates—toggle inactive before adding a replacement window."
        actions={
          <Button iconLeft={<FaPlus />} onClick={openAdd}>
            Add rate schedule
          </Button>
        }
      />

      <div className={styles.page}>
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or date"
              iconLeft={<FaSearch />}
            />
          </div>
          <div className={styles.filters} role="tablist">
            {STATUS_FILTERS.map((f) => {
              const selected = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={[
                    styles.filterChip,
                    selected ? styles.filterChipSelected : ''
                  ].join(' ')}
                  onClick={() => setFilter(f.id)}
                >
                  <span>{f.label}</span>
                  <span className={styles.filterCount}>{counts[f.id] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Card className={styles.tableCard}>
          <CardBody className={styles.tableBody}>
            {loading ? (
              <div className={styles.skeleton}>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={styles.skeletonRow}
                    style={{ animationDelay: `${i * 70}ms` }}
                  />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.empty}>
                <h3>No rate schedules match these filters.</h3>
                <p>Add a schedule or adjust search and status filters.</p>
              </div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Schedule</th>
                      <th>Effective period</th>
                      <th className={styles.rateCol}>0–17 mo</th>
                      <th className={styles.rateCol}>18–29 mo</th>
                      <th className={styles.rateCol}>30–59 mo</th>
                      <th className={styles.rateCol}>60–156 mo</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((rate) => (
                      <tr key={rate.id}>
                        <td className={styles.rateTitleCell}>
                          <div className={styles.rateTitle}>{rate.name || '—'}</div>
                          <div className={styles.rateMeta}>
                            <FaHashtag aria-hidden />#{rate.id}
                          </div>
                        </td>
                        <td className={styles.periodCell}>
                          <FaCalendarAlt className={styles.periodIcon} aria-hidden />
                          {sliceDate(rate.date_from)} → {sliceDate(rate.date_to)}
                        </td>
                        <td className={styles.rateCell}>{formatUsd(rate.rate_0_17m)}</td>
                        <td className={styles.rateCell}>{formatUsd(rate.rate_18_29m)}</td>
                        <td className={styles.rateCell}>{formatUsd(rate.rate_30_59m)}</td>
                        <td className={styles.rateCell}>{formatUsd(rate.rate_60_156m)}</td>
                        <td>
                          <Badge variant={rate.is_active ? 'success' : 'neutral'}>
                            {rate.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className={styles.actionsCell}>
                          <div className={styles.rowActions}>
                            <Button
                              size="sm"
                              variant="ghost"
                              iconLeft={<FaPen />}
                              onClick={() => openEdit(rate)}
                            >
                              Edit
                            </Button>
                            {rate.is_active ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                iconLeft={<FaTrash />}
                                onClick={() => setConfirmInactive(rate)}
                                className={styles.dangerAction}
                              >
                                Inactivate
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={formOpen}
        onClose={closeForm}
        title={form.id ? 'Edit rate schedule' : 'Add rate schedule'}
        description={
          form.id
            ? 'Adjust dates, amounts, or active status. Overlapping active windows are blocked.'
            : 'Define the effective date range and daily amounts for each age band.'
        }
        size="lg"
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          <Input
            label="Schedule name"
            value={form.name}
            onChange={setField('name')}
            iconLeft={<FaUniversity />}
            placeholder="e.g. FY2026 standard rates"
            required
            autoFocus
          />

          <p className={styles.sectionLabel}>Effective dates</p>
          <div className={styles.dateRow}>
            <Input
              label="Start date"
              type="date"
              value={form.date_from}
              onChange={setField('date_from')}
              required
            />
            <Input
              label="End date"
              type="date"
              value={form.date_to}
              onChange={setField('date_to')}
              required
            />
          </div>

          <p className={styles.sectionLabel}>Daily rate by age (months)</p>
          <div className={styles.ratesGrid}>
            <Input
              label="0–17 months"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.rate_0_17m}
              onChange={setField('rate_0_17m')}
              required
            />
            <Input
              label="18–29 months"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.rate_18_29m}
              onChange={setField('rate_18_29m')}
              required
            />
            <Input
              label="30–59 months"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.rate_30_59m}
              onChange={setField('rate_30_59m')}
              required
            />
            <Input
              label="60–156 months"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.rate_60_156m}
              onChange={setField('rate_60_156m')}
              required
            />
          </div>

          <label className={styles.activeRow}>
            <input type="checkbox" checked={!!form.is_active} onChange={setField('is_active')} />
            <span>
              <strong>Active</strong>
              <span className={styles.activeHint}>
                Inactive schedules are ignored by dashboards and voucher calculations.
              </span>
            </span>
          </label>

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} iconLeft={<FaCheck />}>
              {form.id ? 'Save changes' : 'Add schedule'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!confirmInactive}
        onClose={() => (submitting ? null : setConfirmInactive(null))}
        title="Set schedule inactive?"
        description={
          confirmInactive
            ? `"${confirmInactive.name}" will no longer apply to new calculations. You can reactivate it later from the Inactive filter.`
            : ''
        }
        size="sm"
      >
        <div className={styles.confirmActions}>
          <Button variant="secondary" onClick={() => setConfirmInactive(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            iconLeft={<FaTrash />}
            onClick={handleConfirmInactive}
            loading={submitting}
          >
            Set inactive
          </Button>
        </div>
      </Modal>
    </>
  );
}
